import {DomainStore} from 'shared/store';
import {observable, action, computed} from 'mobx';
import {BenefitClass} from 'stores/benefits';
import {Steps} from 'shared/tools';
import {GroupBenefitCoverage, CoverageVolumeTier, CoverageVolumeReduction} from 'stores/benefits';
import _ from 'lodash';
import {endpoints, types} from 'shared/core';

class TaxableBenefitFlowState {
  store = new DomainStore();
  history;
  match;
  backPath;

  @observable benefitClass;
  @observable steps;
  @observable currentLocation;
  @observable errors = {};
  @observable groupBenefitCoverages;

  receiveProps({backPath, match, history}) {
    this.match = match; 
    this.history = history;
    this.backPath = backPath;
  }

  @action async load() {
    await this.store._compose([
      endpoints.BROKER_PORTAL.GROUP_BENEFIT_COVERAGES.with(
        this.match.params.benefitClassId
      )
    ]);

    this.groupBenefitCoverages = this.store._getAll(
      types.GROUP_BENEFIT_COVERAGE, GroupBenefitCoverage
    );

    this.benefitClass = new BenefitClass(
      this.store._getSingle(types.BENEFIT_CLASS)
    );

    this.steps = new Steps(this._initializeSteps());
    this.currentLocation = this.steps.first().location;
  }

  @action async onStepSubmitted(step) {
    const {model, errors} = await this.store.patch(this.currentCoverage);
    this.errors = errors;

    if (model) {
      if (!this.steps.after(step)) {
        this.history.push(this.backPath); 
      }

      this.currentCoverage.update(model);
    } else {
      return false;
    }
  }

  @action async onStepChanged({location}) {
    this.currentLocation = location;
  }

  @action changeVolumeType(volumeType) {
    if (volumeType === this.currentCoverage.volumeType) return;
    if (volumeType === 'tier') {
      this.addCoverageVolumeTier();
    } else {
      this.resetCoverageVolumeTiers();
    }

    this.currentCoverage.updateVolumeType(volumeType);
  }

  @action addCoverageVolumeTier() {
    const order = this.currentCoverage.coverageVolumeTiers.length;
    const tier = new CoverageVolumeTier({order});
    this.currentCoverage.coverageVolumeTiers.push(tier);
  }

  @action removeCoverageVolumeTier(tier) {
    if (this.currentCoverage.coverageVolumeTiers.length === 1) return;
    this.currentCoverage.coverageVolumeTiers.remove(tier);

    this.currentCoverage.coverageVolumeTiers.forEach((tier, index) => {
      tier.merge({order: index});
    });
  }

  @action resetCoverageVolumeTiers() {
    this.currentCoverage.coverageVolumeTiers = [];
  }

  @action addVolumeReduction() {
    const reduction = new CoverageVolumeReduction();
    this.currentCoverage.coverageVolumeReductions.push(reduction);
  }

  @action removeVolumeReduction(reduction) {
    this.currentCoverage.coverageVolumeReductions.remove(reduction);
  }

  @computed get currentCoverage() {
    return _.find(
      this.groupBenefitCoverages,
      { coverageType: this.currentLocation }
    );
  }

  @computed get showCoverageTierRemoveLink() {
    return this.currentCoverage.coverageVolumeTiers.length !== 1;
  }

  _initializeSteps() {
    return _.chain(this.groupBenefitCoverages)
      .reject(coverage => coverage.coverageCategory === 'excluded_coverage')
      .map((coverage, index) => ({
        name: `models.coverage_types.${coverage.coverageType}`,
        location: coverage.coverageType,
        index: index + 1
      }))
      .value();
  }
}

export default TaxableBenefitFlowState;
